/**
 * The Összesítés of §5.11, as a customer email reproduces it.
 *
 * §19.5 step 1: "Customer emails that carry the order reproduce the
 * Összesítés (§5.11)." The same four blocks in the same order, the same
 * gross-or-net rule by buyer type, and the same two things the page withholds
 * — no price per dish, and nothing about production, rounding, margin or
 * capacity.
 *
 * It is built from the payload's own figures, never recomputed. §5.11:
 * "Every total is the sum of the rows above it." An email that re-derived the
 * money could disagree with the invoice that was already issued against it.
 */

/* Run 16 item 2: one formatter for every delivery and pickup time, shared
   with the customer page and the Edge Functions. The package has no
   dependency on `@plt/engine`, so it is reached the way
   `supabase/functions/_shared/engine.ts` reaches it — by path. */
import { eventTimeLabel, type EventTimeLang } from '../../engine/src/eventTime.ts'

import type { Block, Cell } from './layout.ts'
import { ft, longDate, n, qty, time, vatLabel } from './format.ts'
import type { CategoryTotal, OrderLine, OrderPayload, PriceCategory } from './payload.ts'

/** The allergen line of every dish whose allergens are not verified (Barna,
 *  10 October 2026). Never *nincs*: that is a claim only a verified dish makes. */
export const ALLERGENS_BEING_VERIFIED = 'Allergének ellenőrzés alatt'

/** §5.11 point 2, in this order. */
export const CATEGORY_ORDER: readonly PriceCategory[] = ['sos', 'edes', 'gyumolcs', 'ital']

export const CATEGORY_LABEL: Record<PriceCategory, string> = {
  sos: 'Sós',
  edes: 'Édes',
  gyumolcs: 'Gyümölcs',
  ital: 'Italok',
}

export const SERVICE_LABEL = {
  szallitas: 'Szállítás',
  felszereles: 'Felszerelés',
  szemelyzet: 'Személyzet',
} as const

/** §5.11 *Gross and net*: a business buyer reads net, a consumer gross. */
export const shown = (order: OrderPayload, amount: { net: number; gross: number }): number =>
  order.is_business ? amount.net : amount.gross

const right = (text: string, strong = false): Cell => ({ text, align: 'right', strong })

/**
 * §5.11 point 1, one event line:
 * *40 vendég · 2026. október 16., péntek · 12:00 · Munkaebéd*, with
 * *ebből N gluténmentes* when gluten-free guests are above zero.
 */
export function eventLine(order: OrderPayload): string {
  const parts: string[] = []
  parts.push(
    order.gf_guests > 0
      ? `${n(order.guests)} vendég (ebből ${n(order.gf_guests)} gluténmentes)`
      : `${n(order.guests)} vendég`,
  )
  parts.push(longDate(order.event_date))
  const start = time(order.event_start)
  if (start !== '') parts.push(start)
  if (order.occasion_label) parts.push(withMealNote(order.occasion_label, mealNoteOf(order, 'hu')))
  return parts.join(' · ')
}

/**
 * **Run 19 §4 (DECIDED 20261018, Barna).** The meal note the payload carries,
 * in the language this email is written in. Null where the occasion has
 * none, and null on a payload written before run 17 item B11(c) gave
 * `outbox_order_json` the two fields.
 *
 * Nothing here composes the sentence: `public.meal_replacement_note` is the
 * one source and every surface reads it.
 */
export const mealNoteOf = (order: OrderPayload, lang: EmailLang): string | null => {
  const note = lang === 'en' ? order.meal_note_en : order.meal_note
  const text = String(note ?? '').trim()
  return text === '' ? null : text
}

/** §4: the occasion name with its note beside it, or the name alone. */
export const withMealNote = (occasion: string, note: string | null): string =>
  note === null ? occasion : `${occasion} — ${note}`

/**
 * The gluten-free portions inside one category (§5.11 point 2).
 *
 * Run 17 item B5(b): the share sits wherever it is made — on a line that is
 * wholly the gluten-free guests', or inside the quantity of a gluten-free
 * dish the customer already chose. A payload written before 15 October 2026
 * carries no `gf_portions` and falls back to the old reading, which is the
 * right answer for it.
 */
const glutenFreePortions = (lines: OrderLine[]): number =>
  lines.reduce(
    (sum, line) =>
      sum + (line.is_gf_portion ? line.quantity : Math.max(line.gf_portions ?? 0, 0)),
    0,
  )

/**
 * §7.3 and §14. A dish whose allergens Robi has not signed off prints as
 * example data and says so. `allergen_test_marked` is the test-only marking
 * of the interim catalogue; a line carrying it is never verified, whatever
 * `allergen_status` says.
 */
export const allergensAreVerified = (line: OrderLine): boolean =>
  line.allergen_status === 'verified' && line.allergen_test_marked !== true

/**
 * Allergens, always code **and** full name — `8 diófélék`.
 *
 * §11.6 asks for it on a kitchen document and §11.5 asks for it in U5:
 * "dishes, allergen codes and names". There is deliberately one function and
 * one spelling. A short form alongside it is what let U5 print `1, 11` to the
 * same customer U14 wrote `8 diófélék` to.
 */
export function allergenTextLong(line: OrderLine, audience: 'customer' | 'kitchen' = 'customer'): string {
  const parts = (line.allergens ?? []).map((allergen) =>
    allergen.name_hu ? `${allergen.code} ${allergen.name_hu}` : String(allergen.code),
  )
  const verified = allergensAreVerified(line)
  // Only a verified dish prints a plain list, and only a verified dish with
  // an empty list prints *nincs*. Anything else reads *Allergének
  // ellenőrzés alatt* (Barna, 10 October 2026): alone for the customer, and
  // with the unverified list after it, marked as such, for the kitchen, which
  // judges dietary requests by it.
  if (!verified) {
    return audience === 'kitchen' && parts.length > 0
      ? `${ALLERGENS_BEING_VERIFIED} — nem ellenőrzött lista: ${parts.join(' · ')}`
      : ALLERGENS_BEING_VERIFIED
  }

  return parts.length === 0 ? 'allergének: nincs' : `allergének: ${parts.join(' · ')}`
}

/**
 * Run 10 item 7b: a plant-based look-alike name — *cézár*, *omlettes* — is
 * printed in italics with an asterisk, and the view carries one footnote.
 * Customer documents only; the kitchen's tables never call this.
 */
export const PLANT_BASED_LOOKALIKE_NOTE = '* Növényi változat, állati eredetű összetevő nélkül.'

/** Run 12 item 8: the page's English footnote, word for word
 *  (`LOOKALIKE_FOOTNOTE.en` in apps/web/src/page/components.tsx). */
export const PLANT_BASED_LOOKALIKE_NOTE_EN = '* Plant-based version, no animal ingredients.'

export type EmailLang = 'hu' | 'en'

/** Run 12 item 8: an order placed in English reads English; anything else Hungarian. */
export const emailLang = (lang: string | null | undefined): EmailLang => (lang === 'en' ? 'en' : 'hu')

/** Run 12 item F: where the name printed in this language is marked. */
export const nameMarks = (line: OrderLine, lang: EmailLang = 'hu'): [number, number][] =>
  ((lang === 'en' ? line.name_en_marks : line.name_hu_marks) ?? []).filter(
    (pair): pair is [number, number] =>
      Array.isArray(pair) && pair.length === 2 && Number.isInteger(pair[0]) && Number.isInteger(pair[1]) && pair[1] > pair[0],
  )

/** Run 12 item F: whether the printed name carries a marked expression — and
 *  so an asterisk, which is what the footnote follows. Emails print no
 *  description, so the name is the only place a mark can be. */
export const isLookalike = (line: OrderLine, lang: EmailLang = 'hu'): boolean => nameMarks(line, lang).length > 0

/**
 * Run 12 item 8 (DECIDED 20261011): the dish name of a customer email —
 * `name_en` for an order placed in English, `name_hu` for a Hungarian one.
 * The publication gate requires both names, so the fallback is for an old
 * snapshot only. Kitchen documents never call this; they stay Hungarian.
 */
export const dishName = (line: OrderLine, lang: EmailLang = 'hu'): string =>
  lang === 'en' ? (line.name_en ?? line.name_hu ?? line.dish_id) : (line.name_hu ?? line.dish_id)

/**
 * Run 12 item F: a name with its marked expressions in italics and an
 * asterisk directly after each — never the whole name.
 */
export function markedName(name: string, marks: readonly [number, number][]): { text: string; italics: [number, number][] } {
  let text = ''
  let at = 0
  const italics: [number, number][] = []
  for (const [start, end] of [...marks].sort((a, b) => a[0] - b[0])) {
    if (start < at || end > name.length) continue
    text += name.slice(at, start)
    italics.push([text.length, text.length + (end - start)])
    text += name.slice(start, end) + '*'
    at = end
  }
  return { text: text + name.slice(at), italics }
}

/** The dish name as a customer cell, its marked expressions in italics with an asterisk. */
export function dishNameCell(
  line: OrderLine,
  suffix = '',
  extra: { indent?: boolean } = {},
  lang: EmailLang = 'hu',
): Cell {
  const { text, italics } = markedName(dishName(line, lang), nameMarks(line, lang))
  return italics.length > 0 ? { text: `${text}${suffix}`, italics, ...extra } : { text: `${text}${suffix}`, ...extra }
}

/** The footnote, once, when at least one line in the view is a look-alike. */
export const lookalikeNote = (lines: readonly OrderLine[], lang: EmailLang = 'hu'): Block | null =>
  lines.some((line) => isLookalike(line, lang))
    ? { kind: 'note', text: lang === 'en' ? PLANT_BASED_LOOKALIKE_NOTE_EN : PLANT_BASED_LOOKALIKE_NOTE }
    : null

const dishLabel = (line: OrderLine, lang: EmailLang): Cell =>
  dishNameCell(line, line.is_gf_portion ? ' — gluténmentes, külön doboz' : '', { indent: true }, lang)

/** §5.11 point 2, the *Étel* table. */
export function foodTable(order: OrderPayload): Block {
  const priceHead = order.is_business ? 'Ár (nettó)' : 'Ár (bruttó)'
  const rows: Cell[][] = []
  let foodNet = 0
  let foodGross = 0

  for (const category of CATEGORY_ORDER) {
    const total: CategoryTotal | undefined = order.totals_by_category[category]
    const lines = order.lines.filter((line) => line.price_category === category)
    if (total === undefined && lines.length === 0) continue
    const amount = total ?? { quantity: 0, unit: null, net: 0, vat: 0, gross: 0 }
    foodNet += amount.net
    foodGross += amount.gross

    const gf = glutenFreePortions(lines)
    rows.push([
      {
        text:
          gf > 0
            ? `${CATEGORY_LABEL[category]} · ebből gluténmentes: ${qty(gf, amount.unit)}`
            : CATEGORY_LABEL[category],
        strong: true,
      },
      right(qty(amount.quantity, amount.unit), true),
      right(ft(shown(order, amount)), true),
    ])

    for (const line of lines) {
      rows.push([
        dishLabel(line, emailLang(order.lang)),
        right(qty(line.quantity, line.unit)),
        // §5.11 *Quantities*: the customer never sees a price per dish.
        right(''),
      ])
      rows.push([
        { text: allergenTextLong(line), indent: true, muted: true },
        right(''),
        right(''),
      ])
    }
  }

  rows.push([
    { text: 'Étel és ital összesen', strong: true },
    right(''),
    right(ft(order.is_business ? foodNet : foodGross), true),
  ])

  return { kind: 'table', head: ['Kategória', right('Mennyiség'), right(priceHead)], rows }
}

/**
 * §5.11 point 3. *Felszerelés* and *Személyzet* appear only when the order
 * carries a priced item of that kind, which on the automatic lane it never
 * does — staff and equipment go to the enquiry lane (§5.9).
 */
export function serviceTable(order: OrderPayload): Block {
  const rows: Cell[][] = []

  if (order.fulfilment === 'pickup') {
    rows.push([{ text: 'Személyes átvétel' }, right('ingyenes')])
  } else {
    const delivery = order.totals_by_category.szallitas ?? {
      quantity: 1,
      unit: null,
      net: Math.round(order.delivery_fee / (1 + order.vat_pct / 100)),
      vat: 0,
      gross: order.delivery_fee,
    }
    const amount = shown(order, delivery)
    const label = order.zone ? `Szállítás · ${order.zone} jelű zóna` : 'Szállítás'
    // §5.11: *ingyenes* above the free-delivery threshold, *egyedi* in zone C.
    const value = order.zone === 'C' ? 'egyedi' : amount === 0 ? 'ingyenes' : ft(amount)
    rows.push([{ text: label }, right(value)])
  }

  for (const key of ['felszereles', 'szemelyzet'] as const) {
    const amount = order.totals_by_category[key]
    if (amount === undefined) continue
    rows.push([{ text: SERVICE_LABEL[key] }, right(ft(shown(order, amount)))])
  }

  return { kind: 'table', head: ['Szolgáltatás', right(order.is_business ? 'Ár (nettó)' : 'Ár (bruttó)')], rows }
}

/** §5.11 point 4: *Összesen* and *per fő*, gross or net by buyer type. */
export function totalsBlock(order: OrderPayload): Block {
  const guests = Math.max(order.guests, 1)
  const rows: [string, string][] = []
  if (order.is_business) {
    rows.push(['Nettó összesen', ft(order.net)])
    rows.push([vatLabel(order.vat_pct), ft(order.vat)])
    rows.push(['Bruttó összesen, fizetendő', ft(order.gross)])
    rows.push(['Vendégenként, nettó', ft(Math.round(order.net / guests))])
  } else {
    rows.push(['Összesen, bruttó', ft(order.gross)])
    rows.push(['Vendégenként, bruttó', ft(Math.round(order.gross / guests))])
  }
  return { kind: 'facts', rows }
}

/**
 * Run 16 item 2: the delivery or collection time with its date and weekday.
 *
 * The day is the order's **fulfilment** day — the event date for a delivery
 * and an ordinary collection, the business day before for a weekend event
 * (§8.1), which `outbox_order_json` puts on the payload. An older outbox row
 * has no such field and falls back to the event date, which is right for
 * every order but a weekend one.
 *
 * Exported so the English templates and the Hungarian ones read one function
 * and cannot drift apart.
 */
export const fulfilmentWhen = (order: OrderPayload, lang: EventTimeLang): string =>
  eventTimeLabel(lang, order.fulfilment_date ?? order.event_date, order.slot_label)

/** §5.11 point 1, as a block of its own. */
export const eventBlock = (order: OrderPayload): Block => ({
  kind: 'facts',
  rows: [
    ['Rendelésazonosító', order.ref],
    ['Időpont és létszám', eventLine(order)],
    ...(order.event_name ? ([['Rendezvény neve', order.event_name]] as [string, string][]) : []),
    [
      'Teljesítés',
      /* Run 16 item 2 (DECIDED 20261015, Barna): the label is the one that was
         here; the time beside it now carries its date and its weekday, written
         by the one shared formatter in `@plt/engine`. */
      order.fulfilment === 'pickup'
        ? `Személyes átvétel${fulfilmentWhen(order, 'hu') ? ` · ${fulfilmentWhen(order, 'hu')}` : ''}`
        : `Kiszállítás${fulfilmentWhen(order, 'hu') ? ` · ${fulfilmentWhen(order, 'hu')}` : ''}`,
    ],
  ],
})

/**
 * §14, *Gluten-free*, reused verbatim in substance: gluten is individually
 * marked and a gluten-free choice is clearly labelled, and the kitchen is not
 * a certified gluten-free facility. Never a claim of a gluten-free
 * environment. Shown as soon as gluten-free guests are above zero.
 */
/*
 * **Run 19 §14 (Fede's review of 16 October, DECIDED 20261016).** The second
 * sentence is reworded: *Konyhánk nem rendelkezik gluténmentes
 * minősítéssel, ezért cöliákiás vendégek számára nem tudjuk garantálni a
 * keresztszennyeződés-mentességet.* It says what the risk actually is —
 * cross-contamination — rather than *a fully gluten-free environment*,
 * which reads as a claim about the room.
 *
 * *keresztszennyeződés-mentességet* takes the hyphen: a compound of six or
 * more syllables with *-mentes* is written with one. Fede's draft has it as
 * two words.
 *
 * The first sentence is untouched. It is a different statement — how the
 * food is packed — and §14 replaces only the notice that follows it. §14
 * also propagates the new sentence to every surface that carries it: the
 * page, the offer card, the confirmation page, U5, U10 and the PDFs.
 */
export const GLUTEN_FREE_NOTICE =
  'A gluténmentes fogásokat külön, jelölt dobozokban szállítjuk. Konyhánk nem ' +
  'rendelkezik gluténmentes minősítéssel, ezért cöliákiás vendégek számára nem ' +
  'tudjuk garantálni a keresztszennyeződés-mentességet.'

/** §7.3: example allergen data never reads as verified. */
export const ALLERGEN_EXAMPLE_NOTICE =
  'A „példa” jelölésű allergénadatok még nem ellenőrzöttek, tájékoztató jellegűek. ' +
  'Az ellenőrzött listát az allergénadatok jóváhagyása után küldjük.'

/** §5.11 point 4, the closing sentence, as the page says it. */
/*
 * Run 19 §14: Fede's wording for the line under *Összesen*. *Kínálatunk
 * 100%-ban növényi* rather than *Minden fogásunk 100% növényi*, and the
 * estimate names the events Planteen has actually catered.
 *
 * The claim rule of 20261019 (handover §4) permits both *100%-ban növényi*
 * and *100%-ban növényi alapú* here; the sentence keeps the short predicate
 * it has had since run 19, because the page's intro text (§10) ends on the
 * same words, which is what keeps them from being two different claims.
 * What is banned is the bare *100% növényi* this line never said.
 */
export const PLANT_BASED_LINE =
  'Kínálatunk 100%-ban növényi. A mennyiségek szakmai becslések, az általunk ' +
  'lebonyolított rendezvények alapján.'

/**
 * The whole Összesítés: the four blocks of §5.11 in order, with the two
 * notices under them where they apply.
 */
export function osszesites(order: OrderPayload): Block[] {
  /* Run 19 §14: the look-alike footnote moves **directly under *Étel és
     ital összesen***, which is the last row of the food table and the only
     place the asterisks it explains appear. It sat under the *Összesen*
     notes, three blocks below the marks. The wording is unchanged. */
  const footnote = lookalikeNote(order.lines, emailLang(order.lang))
  const blocks: Block[] = [
    { kind: 'heading', text: 'Az Ön rendezvénye' },
    eventBlock(order),
    { kind: 'heading', text: 'Étel' },
    foodTable(order),
    ...(footnote === null ? [] : [footnote]),
    { kind: 'heading', text: 'Szolgáltatás' },
    serviceTable(order),
    { kind: 'heading', text: 'Összesen' },
    totalsBlock(order),
    { kind: 'note', text: PLANT_BASED_LINE },
  ]
  if (order.gf_guests > 0) blocks.push({ kind: 'note', text: GLUTEN_FREE_NOTICE })
  if (order.lines.some((line) => !allergensAreVerified(line))) {
    blocks.push({ kind: 'note', text: ALLERGEN_EXAMPLE_NOTICE })
  }
  return blocks
}
